"use strict";
// A maintenance contract (#515, docs/PRICING.md "Maintenance contracts"): the STM002 Take Off's
// "Maintenance Servicing" columns (R:AI, rows 45–106): one area's servicing for one year. The
// contract over the years is maintenance-contract.ts, the Yearly Summaries yearly-summary.ts. Pure.
//
//   second repaint  X = hours ÷ coats × 2nd repaint coats, Y = litres ÷ coats × 2nd repaint coats   (X48, Y48)
//   one service     S = X × service fraction × (1 + extra %), T likewise from Y                      (S48, T48)
//   service sell    S × (servicing rate + additional $/hr) + T × material rate                       (V48)
//   service cost    S × (servicing cost + additional $/hr), T × paint cost                           (AG48, AF48)
//   hire, scaffold moves: only with "Include access"; fuel levy, other trades: only with "Include prelims"
//
// The template's quirks are copied so the figures match it to the cent (DD, 2026-10-23); each is
// marked QUIRK below and listed in PRICING.md.
Object.defineProperty(exports, "__esModule", { value: true });
exports.SERVICE_ROW = exports.CONTRACT_CATEGORY_LABELS = exports.CONTRACT_CATEGORIES = void 0;
exports.serviceSection = serviceSection;
exports.CONTRACT_CATEGORIES = ['labour', 'materials', 'hire', 'prelims'];
/** Each category as the Yearly Summaries' rows name it. */
exports.CONTRACT_CATEGORY_LABELS = {
    labour: 'Labour',
    materials: 'Paint & Materials',
    hire: 'Hire/Access Equipment',
    prelims: 'Prelims incl minor contractors',
};
/** The ids of a section's serviced rows that aren't surfaces (a surface row's id is its own, or `<surface>:<system>`). */
exports.SERVICE_ROW = { sundries: '#sundries', wash: '#wash', scaffold: '#scaffold' };
const sum = (items, value) => items.reduce((total, item) => total + value(item), 0);
/** Row 48's servicing columns for one row of work. */
function serviceRow(work, extra, setup) {
    const share = setup.serviceFraction * (1 + extra);
    const secondHours = work.coats > 0 ? (work.hours / work.coats) * setup.secondRepaintCoats : 0;
    const secondLitres = work.coats > 0 ? (work.litres / work.coats) * setup.secondRepaintCoats : 0;
    const hours = secondHours * share;
    const litres = secondLitres * share;
    const rate = setup.servicing.sell + setup.extraRate.sell;
    const row = { id: work.id, name: work.name, extra, secondHours, secondLitres, hours, litres, rate, sell: hours * rate + litres * work.material.sell };
    return { row, labourCost: hours * (setup.servicing.cost + setup.extraRate.cost), materialCost: litres * work.material.cost };
}
/** The rows a section services: its surfaces, then the sundries (row 63), the wash (64) and, with access, the scaffold moves (65). Down time and consumables are never serviced. */
function rowsOf(input, priced, rates, setup) {
    const markup = 1 + rates.markupMaterials;
    const standard = rates.paints[0];
    const standardRate = { sell: standard ? standard.costPerLitre * markup : 0, cost: standard?.costPerLitre ?? 0 };
    const noMaterial = { sell: 0, cost: 0 };
    // The surfaces' own hours: approved hours are a review figure for the repaint, not a measurement to service.
    // Each row's coats by its id (a surface, or one of its paint systems, `<surface>:<system>`), never by position.
    const inputs = new Map(input.surfaces.map((s) => [s.id, s]));
    const surfaces = priced.surfaces.map((s) => ({
        id: s.id,
        name: s.name,
        hours: s.hours,
        litres: s.litres,
        coats: inputs.get(s.id)?.coats ?? 0,
        material: { sell: s.materialRate, cost: s.materialRate / markup },
    }));
    const extras = [
        { id: exports.SERVICE_ROW.sundries, name: 'Sundry', hours: priced.sundryHours, litres: priced.sundryLitres, coats: 1, material: standardRate },
        { id: exports.SERVICE_ROW.wash, name: 'Wash', hours: priced.washHours, litres: 0, coats: 1, material: noMaterial },
    ];
    if (setup.includeAccess)
        extras.push({ id: exports.SERVICE_ROW.scaffold, name: 'Scaffold moves', hours: priced.scaffoldHours, litres: 0, coats: 1, material: noMaterial });
    return [...surfaces, ...extras];
}
/**
 * QUIRK (V89): with access included, the template adds the FIRST surface row's service hours × 10
 * (S48 × 10 = X48 × (1 + R48): its second-repaint hours WITH its Extra Service) × the abseil % to the
 * servicing sell as dollars, with no rate and no cost. Checked in Excel with R48 at 20 % (24 Oct 2026).
 */
function abseilQuirk(input, rows, setup) {
    const first = input.surfaces.length ? rows[0] : undefined;
    if (!setup.includeAccess || !first || setup.serviceFraction <= 0)
        return 0;
    return (first.hours / setup.serviceFraction) * (input.abseil ?? 0);
}
/** One section's servicing for one year (the Take Off's R:AI columns, rows 48–90). */
function serviceSection(input, priced, rates, setup) {
    const markupOther = 1 + rates.markupOther;
    const own = setup.rowExtras?.[priced.id] ?? {};
    const worked = rowsOf(input, priced, rates, setup).map((w) => serviceRow(w, own[w.id] ?? setup.extraServicing, setup));
    const rows = worked.map((w) => w.row);
    const hireSell = setup.includeAccess ? sum(priced.access, (a) => a.sell) : 0;
    // QUIRK (row 77): the fuel levy serviced each year is the REPAINT's (its hours × the levy), not the service hours'.
    const otherSell = sum(priced.otherTrades, (o) => o.sell);
    const prelimsSell = setup.includePrelims ? priced.fuelLevy + otherSell : 0;
    const beforeFactor = sum(rows, (r) => r.sell) + hireSell + prelimsSell;
    // V90: the negotiating factor only when the repaint's is positive (QUIRK: a negative factor leaves servicing alone).
    const negotiating = priced.negotiating > 0 ? beforeFactor * rates.negotiatingFactor : 0;
    const abseilSell = abseilQuirk(input, rows, setup);
    const cost = {
        labour: sum(worked, (w) => w.labourCost),
        materials: sum(worked, (w) => w.materialCost),
        hire: hireSell / markupOther,
        prelims: setup.includePrelims ? priced.fuelLevy + otherSell / markupOther : 0,
    };
    return {
        id: priced.id,
        name: priced.name,
        rows,
        hireSell,
        prelimsSell,
        abseilSell,
        negotiating,
        hours: sum(rows, (r) => r.hours),
        litres: sum(rows, (r) => r.litres),
        sell: beforeFactor + negotiating + abseilSell,
        cost,
    };
}
